
import React, { useState } from "react";
import { useTranslations } from "use-intl";
import { MoreHorizontal, Eye, Download, CheckCircle, Edit, Calendar as CalendarIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BankAccountsStatus, Invoice } from "@/types/payments";
import { isInAppPaymentsEnabled } from "@/config/features";
import PayableMatchSuggestionsModal from "@/components/payments/PayableMatchSuggestionsModal";

interface InvoiceActionMenuProps {
  invoice: Invoice;
  onView: (invoice: Invoice) => void;
  onPay?: (invoiceId: string, paymentDate: Date, actualSekAmount?: number, isAcknowledgment?: boolean) => void;
  onCancel?: (invoiceId: string) => void;
  onExport?: (invoiceId: string) => void;
  onDownload?: (invoiceId: string, fileName: string) => void;
  onEditPayment?: (invoiceId: string) => void;
  onConfirmMatch?: (payableId: string, transactionId: string) => Promise<boolean>;
  /** Pass-through — see BankAccountsStatus's docstring (#498 whole-branch review finding I2). */
  hasBankAccounts?: BankAccountsStatus;
}

const InvoiceActionMenu: React.FC<InvoiceActionMenuProps> = ({
  invoice,
  onView,
  onPay,
  onCancel,
  onDownload,
  onEditPayment,
  onConfirmMatch,
  hasBankAccounts
}) => {
  const t = useTranslations("consultantPayables.actions");
  // State for the single merged "Mark as paid" modal (issue #498 — merges the former separate
  // "Paid externally" and "Match to transaction" entry points into one).
  const [matchModalOpen, setMatchModalOpen] = useState(false);

  // Stop click propagation to prevent row click handler from firing
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // The same "Mark as paid" entry for unpaid, overdue and pending invoices.
  const markAsPaidItem = onPay && (
    <DropdownMenuItem onClick={() => setMatchModalOpen(true)} className="flex items-center gap-2">
      <CheckCircle className="h-4 w-4" />
      <span>{t("markAsPaid")}</span>
    </DropdownMenuItem>
  );

  return (
    <div onClick={handleClick}>
      <DropdownMenu>
        <DropdownMenuTrigger className="cursor-pointer hover:bg-muted p-1 rounded-sm focus:outline-none">
          <MoreHorizontal className="h-5 w-5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="z-50 bg-background"
          // Items here open dialogs: returning focus to this trigger as the menu closes would race
          // the dialog's own focus trap, which re-selects its first field and replaces whatever
          // the user has started typing there (FIN-1123).
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          {/* View Details - available for all statuses */}
          <DropdownMenuItem onClick={() => onView(invoice)} className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            <span>{t("viewDetails")}</span>
          </DropdownMenuItem>

          {/* Unpaid and Overdue invoices */}
          {(invoice.status === "unpaid" || invoice.status === "overdue") && (
            <>
              {/* Issue #498 — single "Mark as paid" action, always present, label never changes
                  based on connection/match state. Always opens PayableMatchSuggestionsModal,
                  which internally adapts its content based on hasBankAccounts. This replaces the
                  former two separate menu items ("Paid externally" / "Match to transaction"),
                  which had a latent bug where both could render simultaneously
                  (hasBankAccounts=true && !isInAppPaymentsEnabled()). */}
              {markAsPaidItem}

              {isInAppPaymentsEnabled() && onEditPayment && (
                <DropdownMenuItem onClick={() => onEditPayment(invoice.id)} className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  <span>{t("editPayment")}</span>
                </DropdownMenuItem>
              )}
            </>
          )}

          {/* Pending invoices */}
          {invoice.status === "pending" && (
            <>
              {markAsPaidItem}

              {isInAppPaymentsEnabled() && onCancel && (
                <DropdownMenuItem onClick={() => onCancel(invoice.id)} className="flex items-center gap-2">
                  <Edit className="h-4 w-4" />
                  <span>{t("cancelPayment")}</span>
                </DropdownMenuItem>
              )}
            </>
          )}

          {/* Paid and manually handled invoices */}
          {(invoice.status === "paid" || invoice.status === "manually_handled") && (
            <>
              {/* PR #844 review: a settled payable ('paid' or 'manually_handled') can no longer
                  be reopened/corrected from this menu, in either app or backend — the underlying
                  #498 Task 8 correction/reopen path has been removed (PaymentService now rejects
                  it with a ConflictError). Download PDF is the only action offered here. */}
              {onDownload && invoice.filePath && (
                <DropdownMenuItem
                  onClick={() => onDownload(invoice.id, invoice.fileName || 'invoice.pdf')}
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  <span>{t("downloadPdf")}</span>
                </DropdownMenuItem>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Mark as Paid Modal (issue #462, merged with the former plain date-picker by #498) */}
      <PayableMatchSuggestionsModal
        open={matchModalOpen}
        setOpen={setMatchModalOpen}
        payable={invoice}
        // #498 whole-branch review finding I2: `?? false` (not `!!`) — `!!` would have collapsed
        // 'loading'/'error' (truthy strings) into `true` by accident rather than by design.
        // `?? false` only substitutes the default for a genuinely omitted prop, leaving
        // 'loading'/'error'/boolean values passed through unchanged.
        hasBankAccounts={hasBankAccounts ?? false}
        onPay={onPay}
        onConfirmMatch={onConfirmMatch}
      />
    </div>
  );
};

export default InvoiceActionMenu;
